import { DvaReducer } from '@/models/shared';
import { AnyAction } from 'redux';
import { EffectsCommandMap, Subscription } from 'dva';
import { ConnectState } from '@/models/connect';
import fMessage from '@/components/fMessage';
import { FUtil, FServiceAPI } from '@freelog/tools-lib';
import { history } from '@@/core/history';
import { PolicyFullInfo_Type } from '@/type/contractTypes';
import fPromiseModalConfirm from '@/components/fPromiseModalConfirm';
import { fSetHotspotTooltipVisible } from '@/components/FHotspotTooltip';
import dayjs from 'dayjs';
import {
  fetchAndSortPolicies,
  addExhibitPolicy,
  updateExhibitPolicyStatus,
  updateExhibitOnlineStatus,
  updateExhibitBaseInfo,
  saveExhibitIntroduction,
  updateRewrite,
} from '@/models/shared/exhibitHelpers';

export interface CreatedCollectionExhibitInfoPageModelState {
  pageLoading: boolean;

  exhibit_ID: string;
  exhibit_Name: string;
  exhibit_Title: string;
  exhibit_ReourceType: string[];
  exhibit_Online: boolean;
  exhibit_IsAuth: boolean;
  exhibit_AuthErrorText: string;
  exhibit_BelongNode_ID: number;
  exhibit_BelongNode_Name: string;
  exhibit_BelongNode_Title: string;
  exhibit_BelongNode_ActiveThemeId: string;
  exhibit_Cover: string;
  exhibit_ExhibitIntroduction: string;
  exhibit_ExhibitTags: string[];
  policy_List: PolicyFullInfo_Type[];
  settingUnfold: boolean;
  customOptions: {
    key: string;
    name: string;
    value: string;
    description: string;
  }[];

  collectionItemsSetting: {
    collection_sort_list: 'collection_sort_ascending' | 'collection_sort_descending';
  };
  collectionItems_keywords: string;
  collectionItems: {
    id: string;
    itemID: string;
    itemTitle: string;
    itemSerial: number;
    exhibtInfo: {
      id: string;
      cover: string;
      name: string;
      title: string;
      intro: string;
      resourceID: string;
      resourceType: string[];
      releaseDateTime: string;
      isAuth: boolean;
      authErrorText: string;
    };
  }[];
  collectionItems_totalCount: number;
  collectionItems_pageCurrent: number;
  collectionItems_pageSize: number;
  collectionItems_state: 'loading' | 'noData' | 'noSearchResult' | 'loaded';
}

export interface ChangeAction extends AnyAction {
  type: 'change' | 'createdCollectionExhibitInfoPage/change';
  payload: Partial<CreatedCollectionExhibitInfoPageModelState>;
}

export interface OnMountPageAction extends AnyAction {
  type: 'createdCollectionExhibitInfoPage/onMountPage';
  payload: {
    exhibitID: string;
  };
}

export interface OnUnmountPageAction extends AnyAction {
  type: 'createdCollectionExhibitInfoPage/onUnmountPage';
}

export interface FetchInfoAction extends AnyAction {
  type: 'fetchInfo' | 'createdCollectionExhibitInfoPage/fetchInfo';
}

export interface Fetch_CollectionItems_Action extends AnyAction {
  type: 'fetch_CollectionItems' | 'createdCollectionExhibitInfoPage/fetch_CollectionItems';
}

export interface AddAPolicyAction extends AnyAction {
  type: 'createdCollectionExhibitInfoPage/addAPolicy';
  payload: {
    title: string;
    text: string;
  };
}

export interface UpdateAPolicyAction extends AnyAction {
  type: 'createdCollectionExhibitInfoPage/updateAPolicy';
  payload: {
    id: string;
    status: 0 | 1;
  };
}

export interface UpdateBaseInfoAction extends AnyAction {
  type: 'createdCollectionExhibitInfoPage/updateBaseInfo';
  payload: Partial<
    Pick<
      CreatedCollectionExhibitInfoPageModelState,
      // 'exhibit_Cover' | 'side_ExhibitTitle' | 'side_ExhibitTags'
      'exhibit_Cover' | 'exhibit_ExhibitIntroduction' | 'exhibit_ExhibitTags' | 'exhibit_Title'
    >
  >;
}

export interface UpdateStatusAction extends AnyAction {
  type: 'createdCollectionExhibitInfoPage/updateStatus';
  payload: 0 | 1;
}

export interface OnSave_ExhibitIntroduction_Action extends AnyAction {
  type: 'createdCollectionExhibitInfoPage/onSave_ExhibitIntroduction';
  payload: {
    value: string;
  };
}

export interface OnSync_Options_Action extends AnyAction {
  type: 'createdCollectionExhibitInfoPage/onSync_Options';
  payload?: {
    successMessage?: string;
  };
}

export interface CreatedCollectionExhibitInfoPageModelType {
  namespace: 'createdCollectionExhibitInfoPage';
  state: CreatedCollectionExhibitInfoPageModelState;
  effects: {
    onMountPage: (action: OnMountPageAction, effects: EffectsCommandMap) => void;
    onUnmountPage: (action: OnUnmountPageAction, effects: EffectsCommandMap) => void;
    fetchInfo: (action: FetchInfoAction, effects: EffectsCommandMap) => void;
    fetch_CollectionItems: (
      action: Fetch_CollectionItems_Action,
      effects: EffectsCommandMap,
    ) => void;
    addAPolicy: (action: AddAPolicyAction, effects: EffectsCommandMap) => void;
    updateAPolicy: (action: UpdateAPolicyAction, effects: EffectsCommandMap) => void;
    updateBaseInfo: (action: UpdateBaseInfoAction, effects: EffectsCommandMap) => void;
    updateStatus: (action: UpdateStatusAction, effects: EffectsCommandMap) => void;
    onSave_ExhibitIntroduction: (
      action: OnSave_ExhibitIntroduction_Action,
      effects: EffectsCommandMap,
    ) => void;
    onSync_Options: (action: OnSync_Options_Action, effects: EffectsCommandMap) => void;
  };
  reducers: {
    change: DvaReducer<CreatedCollectionExhibitInfoPageModelState, ChangeAction>;
  };
  subscriptions: {
    setup: Subscription;
  };
}

const initStates: CreatedCollectionExhibitInfoPageModelState = {
  pageLoading: true,

  exhibit_ID: '',
  exhibit_Name: '',
  exhibit_Title: '',
  exhibit_ReourceType: [],
  exhibit_Online: false,
  exhibit_IsAuth: true,
  exhibit_AuthErrorText: '',
  exhibit_BelongNode_ID: -1,
  exhibit_BelongNode_Name: '',
  exhibit_BelongNode_Title: '',
  exhibit_BelongNode_ActiveThemeId: '',
  exhibit_Cover: '',
  policy_List: [],

  exhibit_ExhibitIntroduction: '',
  exhibit_ExhibitTags: [],
  settingUnfold: false,
  customOptions: [],

  collectionItemsSetting: {
    collection_sort_list: 'collection_sort_ascending',
  },
  collectionItems_keywords: '',
  collectionItems: [],
  collectionItems_totalCount: 0,
  collectionItems_pageCurrent: 1,
  collectionItems_pageSize: 10,
  // collectionItems_inited: false,
  collectionItems_state: 'loading',
};

const Model: CreatedCollectionExhibitInfoPageModelType = {
  namespace: 'createdCollectionExhibitInfoPage',
  state: initStates,
  effects: {
    *onMountPage({ payload }: OnMountPageAction, { put, call }: EffectsCommandMap) {
      yield put<ChangeAction>({
        type: 'change',
        payload: {
          exhibit_ID: payload.exhibitID,
        },
      });

      yield put<FetchInfoAction>({
        type: 'fetchInfo',
      });
    },
    *onUnmountPage({}: OnUnmountPageAction, { put }: EffectsCommandMap) {
      yield put<ChangeAction>({
        type: 'change',
        payload: {
          ...initStates,
        },
      });
    },
    *fetchInfo({}: FetchInfoAction, { call, select, put }: EffectsCommandMap) {
      const { createdCollectionExhibitInfoPage }: ConnectState = yield select(
        ({ createdCollectionExhibitInfoPage }: ConnectState) => ({
          createdCollectionExhibitInfoPage,
        }),
      );
      if (!createdCollectionExhibitInfoPage.exhibit_ID) {
        return;
      }
      const params: Parameters<typeof FServiceAPI.Exhibit.presentableDetails>[0] = {
        presentableId: createdCollectionExhibitInfoPage.exhibit_ID,
        isLoadCustomPropertyDescriptors: 1,
        isLoadPolicyInfo: 1,
        isTranslate: 1,
        isLoadVersionProperty: 1,
      };

      yield put<ChangeAction>({
        type: 'change',
        payload: {
          exhibit_ID: '',
        },
      });
      const {
        data: data_PresentableDetails,
      }: {
        data: {
          nodeId: number;
          userId: number;
          presentableId: string;
          presentableName: string;
          resourceInfo: any;
          resourceCustomPropertyDescriptors: {
            candidateItems: string[];
            defaultValue: string;
            key: string;
            name: string;
            remark: string;
            type: 'readonlyText' | 'select' | 'editableText';
          }[];
          coverImages: string[];
          onlineStatus: number;
          policies: any[];
          presentableTitle: string;
          presentableIntro: string;
          resolveResources: any[];
          tags: string[];
          version: string;
          resourceSystemProperty: {
            [k: string]: string;
          };
          resourceSystemPropertyDescriptors: {
            defaultValue: number | string;
            key: string;
            name: string;
            remark: string;
            valueDisplay: string;
            valueUnit: string;
          }[];
          presentableRewriteProperty: {
            key: string;
            remark: string;
            value: string;
          }[];
          versionProperty?: {
            catalogueProperty?: {
              collection_sort_list?: 'collection_sort_descending' | 'collection_sort_ascending';
              collection_item_count?: number;
            };
          };
        };
      } = yield call(FServiceAPI.Exhibit.presentableDetails, params);
      if (
        !data_PresentableDetails ||
        data_PresentableDetails.userId !== FUtil.Tool.getUserIDByCookies()
      ) {
        history.replace(FUtil.LinkTo.exception403({}));
        return;
      }

      const params3: Parameters<typeof FServiceAPI.Node.details>[0] = {
        nodeId: data_PresentableDetails.nodeId,
      };

      const { data: data_NodeDetails } = yield call(FServiceAPI.Node.details, params3);
      // console.log(data3, 'data90j23rlkfjasdfa');

      if (data_NodeDetails?.status === -1) {
        history.replace(FUtil.LinkTo.nodeDeleted({ nodeID: data_PresentableDetails.nodeId }));
        return;
      }

      if ((data_NodeDetails.status & 4) === 4) {
        history.replace(FUtil.LinkTo.nodeFreeze({ nodeID: data_PresentableDetails.nodeId }));
        return;
      }

      // 要禁用的键
      const disabledRewriteKeys = [
        ...data_PresentableDetails.resourceCustomPropertyDescriptors.map((i) => i.key),
      ];

      // 获取展品授权结果
      const params1: Parameters<typeof FServiceAPI.Exhibit.batchAuth>[0] = {
        nodeId: data_PresentableDetails.nodeId,
        authType: 3,
        presentableIds: data_PresentableDetails.presentableId,
      };
      const { data: data_ExhibitBatchAuthResults } = yield call(
        FServiceAPI.Exhibit.batchAuth,
        params1,
      );

      const policies: PolicyFullInfo_Type[] = [...data_PresentableDetails.policies];
      policies.reverse();

      policies.sort((a, b) => {
        return a.status === 1 && b.status === 0 ? -1 : 0;
      });

      yield put<ChangeAction>({
        type: 'change',
        payload: {
          pageLoading: false,
          exhibit_BelongNode_ID: data_PresentableDetails.nodeId,
          exhibit_BelongNode_Name: data_NodeDetails.nodeName,
          exhibit_BelongNode_Title: data_NodeDetails.nodeTitle,
          exhibit_BelongNode_ActiveThemeId: data_NodeDetails.nodeThemeId,
          exhibit_ID: data_PresentableDetails.presentableId,
          exhibit_Name: data_PresentableDetails.presentableName,
          exhibit_Title: data_PresentableDetails.presentableTitle,
          exhibit_ReourceType: data_PresentableDetails.resourceInfo?.resourceType || ['类型错误'],
          exhibit_Online: data_PresentableDetails.onlineStatus === 1,
          exhibit_IsAuth: data_ExhibitBatchAuthResults
            ? data_ExhibitBatchAuthResults[0].isAuth
            : true,
          exhibit_AuthErrorText: data_ExhibitBatchAuthResults
            ? data_ExhibitBatchAuthResults[0].error
            : '',
          exhibit_Cover: data_PresentableDetails.coverImages[0] || '',

          policy_List: policies,

          exhibit_ExhibitIntroduction: data_PresentableDetails.presentableIntro || '',
          exhibit_ExhibitTags: data_PresentableDetails.tags,
          customOptions: (data_PresentableDetails.presentableRewriteProperty as any[])
            .filter((pr: any) => !disabledRewriteKeys.includes(pr.key))
            .map<CreatedCollectionExhibitInfoPageModelState['customOptions'][number]>((pr) => {
              return {
                key: pr.key,
                name: pr.name,
                value: pr.value,
                description: pr.remark,
                valueInput: pr.value,
                valueInputError: '',
              };
            }),

          collectionItemsSetting: {
            collection_sort_list:
              data_PresentableDetails.versionProperty?.catalogueProperty?.collection_sort_list ||
              'collection_sort_ascending',
          },
          collectionItems_pageSize: 10,
        },
      });

      yield put<Fetch_CollectionItems_Action>({
        type: 'fetch_CollectionItems',
      });
    },
    *fetch_CollectionItems(
      {}: Fetch_CollectionItems_Action,
      { select, call, put }: EffectsCommandMap,
    ) {
      const { createdCollectionExhibitInfoPage }: ConnectState = yield select(
        ({ createdCollectionExhibitInfoPage }: ConnectState) => ({
          createdCollectionExhibitInfoPage,
        }),
      );

      let data_collectionItems: {
        createDate: string;
        itemId: string;
        itemTitle: string;
        sortId: number;
        mountPresentableInfo: {
          coverImages: string[];
          presentableId: string;
          presentableName: string;
          resourceInfo: {
            resourceId: string;
            resourceName: string;
            resourceType: string[];
          };
          presentableIntro: string;
        };
        resourceFirstVersionReleaseDate: string;
      }[] = [];

      const parmas: Parameters<typeof FServiceAPI.Exhibit.getItemsFromPresentableCollection>[0] = {
        presentableId: createdCollectionExhibitInfoPage.exhibit_ID,
        skip:
          (createdCollectionExhibitInfoPage.collectionItems_pageCurrent - 1) *
          createdCollectionExhibitInfoPage.collectionItems_pageSize,
        limit: createdCollectionExhibitInfoPage.collectionItems_pageSize,
        sortType:
          createdCollectionExhibitInfoPage.collectionItemsSetting.collection_sort_list ===
          'collection_sort_descending'
            ? -1
            : 1,
        isLoadLatestVersionInfo: 1,
        keywords: createdCollectionExhibitInfoPage.collectionItems_keywords,
      };

      const {
        ret,
        errCode,
        data: data_Items,
      }: {
        ret: number;
        errCode: number;
        data: {
          skip: number;
          limit: number;
          totalItem: number;
          dataList: typeof data_collectionItems;
        };
      } = yield call(FServiceAPI.Exhibit.getItemsFromPresentableCollection, parmas);

      if (data_Items.dataList.length > 0) {
        data_collectionItems = data_Items.dataList;
      } else if (createdCollectionExhibitInfoPage.collectionItems_pageCurrent > 1) {
        yield put<ChangeAction>({
          type: 'change',
          payload: {
            collectionItems_pageCurrent: 1,
          },
        });

        const parmas: Parameters<typeof FServiceAPI.Exhibit.getItemsFromPresentableCollection>[0] =
          {
            presentableId: createdCollectionExhibitInfoPage.exhibit_ID,
            // skip:
            //   (createdCollectionExhibitInfoPage.collectionItems_pageCurrent - 1) *
            //   createdCollectionExhibitInfoPage.collectionItems_pageSize,
            skip: 0,
            limit: createdCollectionExhibitInfoPage.collectionItems_pageSize,
            sortType:
              createdCollectionExhibitInfoPage.collectionItemsSetting.collection_sort_list ===
              'collection_sort_descending'
                ? -1
                : 1,
            isLoadLatestVersionInfo: 1,
            keywords: createdCollectionExhibitInfoPage.collectionItems_keywords as any,
          };

        const {
          ret,
          errCode,
          data: data_Items1,
        }: {
          ret: number;
          errCode: number;
          data: {
            skip: number;
            limit: number;
            totalItem: number;
            dataList: typeof data_collectionItems;
          };
        } = yield call(FServiceAPI.Exhibit.getItemsFromPresentableCollection, parmas);

        data_collectionItems = data_Items1.dataList;
      }

      let batchAuthPs: {
        error: string;
        isAuth: boolean;
        presentableId: string;
      }[] = [];
      if (data_collectionItems.length > 0) {
        const params1: Parameters<typeof FServiceAPI.Exhibit.batchAuth>[0] = {
          nodeId: createdCollectionExhibitInfoPage.exhibit_BelongNode_ID,
          authType: 3,
          presentableIds: data_collectionItems
            .map<string>((dl) => {
              return dl.mountPresentableInfo.presentableId;
            })
            .join(','),
        };
        const {
          data: data1,
        }: {
          data: {
            error: string;
            isAuth: boolean;
            presentableId: string;
          }[];
        } = yield call(FServiceAPI.Exhibit.batchAuth, params1);
        batchAuthPs = data1;
      }

      yield put<ChangeAction>({
        type: 'change',
        payload: {
          collectionItems: data_collectionItems.map<
            CreatedCollectionExhibitInfoPageModelState['collectionItems'][number]
          >((i) => {
            const authInfo = batchAuthPs.find((bap) => {
              return bap.presentableId === i.mountPresentableInfo.presentableId;
            });
            return {
              id: i.itemId,
              itemID: i.itemId,
              itemTitle: i.itemTitle,
              itemSerial: i.sortId,
              exhibtInfo: {
                id: i.mountPresentableInfo.presentableId,
                cover: i.mountPresentableInfo.coverImages[0],
                name:
                  createdCollectionExhibitInfoPage.exhibit_BelongNode_Name +
                  '/' +
                  i.mountPresentableInfo.presentableName,
                title: i.mountPresentableInfo.presentableName,
                intro: i.mountPresentableInfo.presentableIntro,
                resourceID: i.mountPresentableInfo.resourceInfo.resourceId,
                resourceType: i.mountPresentableInfo.resourceInfo.resourceType,
                releaseDateTime: dayjs(i.resourceFirstVersionReleaseDate).format(FUtil.Predefined.momentDateFormat),
                isAuth: authInfo?.isAuth ?? true,
                authErrorText: authInfo?.error ?? '',
              },
            };
          }),
          collectionItems_state:
            data_Items.totalItem > 0
              ? 'loaded'
              : createdCollectionExhibitInfoPage.collectionItems_keywords === ''
              ? 'noData'
              : 'noSearchResult',
          collectionItems_totalCount: data_Items.totalItem,
        },
      });
    },
    *addAPolicy({ payload }: AddAPolicyAction, { call, select, put }: EffectsCommandMap) {
      const { createdCollectionExhibitInfoPage }: ConnectState = yield select(
        ({ createdCollectionExhibitInfoPage }: ConnectState) => ({
          createdCollectionExhibitInfoPage,
        }),
      );

      const success: boolean = yield call(addExhibitPolicy, createdCollectionExhibitInfoPage.exhibit_ID, payload.title, payload.text);
      if (!success) return;

      fSetHotspotTooltipVisible('exhibitDetailPage.onlineSwitch', {
        value: true,
        effectiveImmediately: true,
        onlyNullish: true,
      });

      setTimeout(() => {
        fSetHotspotTooltipVisible('exhibitDetailPage.onlineSwitch', {
          value: false,
          effectiveImmediately: false,
          onlyNullish: false,
        });
      });

      if (!createdCollectionExhibitInfoPage.exhibit_Online) {
        const online: boolean = yield call(fPromiseModalConfirm, {
          title: '展品待上架',
          description: '将展品上架开放授权，为你带来更多收益',
          okText: '立即上架',
          cancelText: '暂不上架',
        });

        if (online) {
          yield call(updateExhibitOnlineStatus, createdCollectionExhibitInfoPage.exhibit_ID, 1);
          fSetHotspotTooltipVisible('exhibitDetailPage.onlineSwitch', {
            value: false,
            effectiveImmediately: true,
            onlyNullish: false,
          });
        }
      }

      const { policies, onlineStatus }: { policies: any[]; onlineStatus: number } = yield call(fetchAndSortPolicies, createdCollectionExhibitInfoPage.exhibit_ID);

      yield put<ChangeAction>({
        type: 'change',
        payload: {
          policy_List: policies,
          exhibit_Online: onlineStatus === 1,
        },
      });
    },
    *updateAPolicy({ payload }: UpdateAPolicyAction, { call, select, put }: EffectsCommandMap) {
      const { createdCollectionExhibitInfoPage }: ConnectState = yield select(
        ({ createdCollectionExhibitInfoPage }: ConnectState) => ({
          createdCollectionExhibitInfoPage,
        }),
      );
      yield call(updateExhibitPolicyStatus, createdCollectionExhibitInfoPage.exhibit_ID, payload.id, payload.status);

      const { policies }: { policies: any[] } = yield call(fetchAndSortPolicies, createdCollectionExhibitInfoPage.exhibit_ID);

      yield put<ChangeAction>({
        type: 'change',
        payload: {
          policy_List: policies,
        },
      });
    },
    *updateBaseInfo({ payload }: UpdateBaseInfoAction, { select, call, put }: EffectsCommandMap) {
      const { createdCollectionExhibitInfoPage }: ConnectState = yield select(
        ({ createdCollectionExhibitInfoPage }: ConnectState) => ({
          createdCollectionExhibitInfoPage,
        }),
      );

      const result: { success: boolean; msg?: string } = yield call(updateExhibitBaseInfo, createdCollectionExhibitInfoPage.exhibit_ID, {
        presentableTitle: payload.exhibit_Title,
        tags: payload.exhibit_ExhibitTags,
        // @ts-ignore
        presentableIntro: payload.exhibit_ExhibitIntroduction || undefined,
        coverImages: payload.exhibit_Cover ? [payload.exhibit_Cover] : undefined,
      });
      if (!result.success) {
        fMessage(result.msg!, 'error');
        return;
      }
      yield put<ChangeAction>({
        type: 'change',
        payload,
      });
    },
    *updateStatus({ payload }: UpdateStatusAction, { call, select, put }: EffectsCommandMap) {
      const { createdCollectionExhibitInfoPage }: ConnectState = yield select(
        ({ createdCollectionExhibitInfoPage }: ConnectState) => ({
          createdCollectionExhibitInfoPage,
        }),
      );
      const success: boolean = yield call(updateExhibitOnlineStatus, createdCollectionExhibitInfoPage.exhibit_ID, payload);
      if (!success) {
        fMessage('上线失败', 'error');
        return;
      }
      yield put<FetchInfoAction>({
        type: 'fetchInfo',
      });
    },
    *onSave_ExhibitIntroduction(
      { payload }: OnSave_ExhibitIntroduction_Action,
      { select, call, put }: EffectsCommandMap,
    ) {
      const { createdCollectionExhibitInfoPage }: ConnectState = yield select(
        ({ createdCollectionExhibitInfoPage }: ConnectState) => ({
          createdCollectionExhibitInfoPage,
        }),
      );
      const oldText: string = createdCollectionExhibitInfoPage.exhibit_ExhibitIntroduction;
      yield put<ChangeAction>({
        type: 'change',
        payload: {
          exhibit_ExhibitIntroduction: payload.value,
        },
      });

      const result: { success: boolean; msg?: string } = yield call(saveExhibitIntroduction, createdCollectionExhibitInfoPage.exhibit_ID, payload.value);
      if (!result.success) {
        fMessage(result.msg!, 'error');
        yield put<ChangeAction>({
          type: 'change',
          payload: {
            exhibit_ExhibitIntroduction: oldText,
          },
        });
        return;
      }
    },
    *onSync_Options({ payload }: OnSync_Options_Action, { select, call, put }: EffectsCommandMap) {
      const { createdCollectionExhibitInfoPage }: ConnectState = yield select(
        ({ createdCollectionExhibitInfoPage }: ConnectState) => ({
          createdCollectionExhibitInfoPage,
        }),
      );

      const {
        data,
        errCode,
        ret,
        msg,
      }: {
        data: boolean;
        errCode: number;
        msg: string;
        ret: number;
      } = yield call(updateRewrite, {
        exhibit_ID: createdCollectionExhibitInfoPage.exhibit_ID,
        inheritOptions: [],
        customOptions: createdCollectionExhibitInfoPage.customOptions,
      });
      if (ret !== 0 || errCode !== 0 || !data) {
        return fMessage(msg, 'error');
      }
      if (payload?.successMessage) {
        fMessage(payload?.successMessage);
      }
    },
  },
  reducers: {
    change(state, { payload }) {
      return {
        ...state,
        ...payload,
      };
    },
  },
  subscriptions: {
    setup({}) {},
  },
};

export default Model;

export type { HandleRelationParams, HandleRelationResult } from '@/models/shared/exhibitHelpers';
export { handleRelation, getAllContracts } from '@/models/shared/exhibitHelpers';
